import { Dialog } from "construct-ui";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import IncentiveSchemeVariableModel from "../../models/incentive-scheme-variable/incentive-scheme-variable.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Scheme Create / Edit Dialog
 *
 * Creates a scheme as DRAFT, or updates an existing one. Activation is a separate
 * action on the parent view, not a status field here - activating supersedes the
 * previous version and must not be a side effect of a normal save.
 *
 * @author FFIS
 */
export default function IncentiveSchemeEditDialog() {
  ViewBase.call(this);

  const FORM_ID = 'IncentiveSchemeEditForm';
  const UPDATE_ACTION = 'UPDATE';

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  /* The vocabulary is fixed by IncentiveSchemeMaster's own documentation - WHOLE_UNIT |
     TWO_DECIMAL | NONE, and FINAL_ONLY | PER_COMPONENT. An earlier version of this file offered
     ROUND_HALF_UP / TRUNCATE / EVERY_STEP, none of which the engine recognises.

     Rounding is NOT a cosmetic setting: the capping workbook pays to the cent (985,836.69), so a
     scheme set to WHOLE_UNIT will not reproduce it. Rounding is unspecified in every source
     document and is a classic payout dispute - see Q-E4. */
  const ROUNDING_RULES = [
    { key: 'TWO_DECIMAL', value: 'TWO_DECIMAL' },
    { key: 'WHOLE_UNIT', value: 'WHOLE_UNIT' },
    { key: 'NONE', value: 'NONE' },
  ];

  const ROUNDING_STAGES = [
    { key: 'FINAL_ONLY', value: 'FINAL_ONLY' },
    { key: 'PER_COMPONENT', value: 'PER_COMPONENT' },
  ];

  const DEFAULT_PAYLOAD = {
    productGroupCode: '',
    division: '',
    schemeCode: '',
    schemeName: '',
    schemeVersion: '',
    calculatorCode: '',
    positionTierScope: '',
    currency: '',
    roundingRule: 'WHOLE_UNIT',
    roundingStage: 'FINAL_ONLY',
    periodFrom: '',
    periodTo: '',
    remark: '',
  };

  let close = () => { };
  let action = '';
  let isUpdating = false;
  let schemeId = 0;
  let calculators = [];
  let payload = structuredClone(DEFAULT_PAYLOAD);

  const handleSubmit = (e) => {
    if (!this.validateForm(e, `#${FORM_ID}`)) {
      return false;
    }

    this.showLoading();

    const request = isUpdating
      ? IncentiveSchemeVariableModel.updateScheme(schemeId, payload)
      : IncentiveSchemeVariableModel.createScheme(payload);

    request
      .then((result) => {
        const trans = Array.isArray(result) ? result[0] : result?.transResult?.[0];
        this.showMessage(
          this.getText(trans?.code, trans?.message),
          trans?.transStatus,
          () => { close(true); });
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  /** yyyyMMdd, which is what every date parameter in these procedures expects. */
  const dateField = (labelCode, fallback, prop, required) => m('.col-md-6.position-relative.mb-4.g-2',
    m(`label.form-label${required ? '.mandatory' : ''}[for=${prop}]`,
      this.getText(labelCode, fallback)),
    m(`input.form-control.form-control-bg[pattern=\\d{8}]${required ? '[required]' : ''}`, {
      id: prop,
      placeholder: 'YYYYMMDD',
      maxlength: 8,
      value: payload[prop] ?? '',
      onchange: (e) => { payload[prop] = e.target.value.trim(); },
    }),
    m('.invalid-tooltip', this.getText('LT00000082', 'Enter a date as YYYYMMDD')));

  const textField = (labelCode, fallback, prop, required, maxlength) =>
    m('.col-md-6.position-relative.mb-4.g-2',
      m(`label.form-label${required ? '.mandatory' : ''}[for=${prop}]`,
        this.getText(labelCode, fallback)),
      m(`input.form-control.form-control-bg${required ? '[required]' : ''}`, {
        id: prop,
        maxlength: maxlength || 200,
        value: payload[prop] ?? '',
        onchange: (e) => { payload[prop] = e.target.value.trim(); },
      }),
      m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')));

  return {
    oninit: (vnode) => {
      if (vnode.attrs.closeDialog) {
        close = vnode.attrs.closeDialog;
      }
      action = vnode.attrs.action;
      isUpdating = action === UPDATE_ACTION;
      schemeId = vnode.attrs.schemeId || 0;
      calculators = vnode.attrs.calculators || [];

      if (isUpdating && vnode.attrs.data) {
        // Copy only the editable fields. Posting back statusCode, audit columns or
        // approval fields would either be ignored or, worse, look like this form
        // owns them - it does not.
        const s = vnode.attrs.data;
        payload = {
          productGroupCode: s.productGroupCode || '',
          division: s.division || '',
          schemeCode: s.schemeCode || '',
          schemeName: s.schemeName || '',
          schemeVersion: s.schemeVersion || '',
          calculatorCode: s.calculatorCode || '',
          positionTierScope: s.positionTierScope || '',
          currency: s.currency || '',
          roundingRule: s.roundingRule || 'WHOLE_UNIT',
          roundingStage: s.roundingStage || 'FINAL_ONLY',
          periodFrom: s.periodFrom || '',
          periodTo: s.periodTo || '',
          remark: s.remark || '',
        };
      } else {
        payload = structuredClone(DEFAULT_PAYLOAD);
      }
    },

    view: (vnode) => {
      return m(Dialog, {
        class: 'cui-dialog-lg',
        isOpen: vnode.attrs.isOpen,
        closeOnOutsideClick: false,
        onClose: () => { vnode.attrs.closeDialog(false); },
        title: isUpdating
          ? this.getText('LT00000128', 'Edit')
          : this.getText('LT00000058', 'Create'),
        content: [
          m('container',
            m('.card',
              m('.card-body',
                m('form.needs-validation.row.g-2[novalidate]',
                  { id: FORM_ID, onsubmit: handleSubmit },

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label.mandatory[for=productGroupCode]',
                      this.getText('LT00000008', 'Product Group')),
                    m(SingleSelectComponent, {
                      id: 'productGroupCode',
                      items: PRODUCT_GROUPS,
                      selectedItem: payload.productGroupCode,
                      keyOnly: true,
                      required: true,
                      onSelect: (item) => { payload.productGroupCode = item; },
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required'))),

                  textField('LT00000009', 'Division', 'division', false, 50),
                  textField('LT00000576', 'Scheme Code', 'schemeCode', true, 50),
                  textField('LT00000002', 'Scheme Name', 'schemeName', true, 200),
                  textField('LT00000003', 'Version', 'schemeVersion', true, 20),

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label.mandatory[for=calculatorCode]',
                      this.getText('LT00000005', 'Calculator')),
                    m(SingleSelectComponent, {
                      id: 'calculatorCode',
                      items: calculators,
                      selectedItem: payload.calculatorCode,
                      keyOnly: true,
                      filterable: true,
                      required: true,
                      onSelect: (item) => { payload.calculatorCode = item; },
                    }),
                    // A scheme naming a calculator the engine does not have cannot
                    // run, so this is a pick-list rather than free text.
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required'))),

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label.mandatory[for=currency]',
                      this.getText('LT00000006', 'Currency')),
                    m('input.form-control.form-control-bg[required][maxlength=3][minlength=3]', {
                      id: 'currency',
                      placeholder: 'IDR / THB / VND',
                      value: payload.currency,
                      onchange: (e) => {
                        payload.currency = e.target.value.trim().toUpperCase();
                      },
                    }),
                    m('.invalid-tooltip',
                      this.getText('LT00000083', 'Enter a 3-letter currency code'))),

                  textField('LT00000023', 'Position Tier Scope', 'positionTierScope', false, 30),

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label[for=roundingRule]',
                      this.getText('LT00000084', 'Rounding Rule')),
                    m(SingleSelectComponent, {
                      id: 'roundingRule',
                      items: ROUNDING_RULES,
                      selectedItem: payload.roundingRule,
                      keyOnly: true,
                      onSelect: (item) => { payload.roundingRule = item; },
                    })),

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label[for=roundingStage]',
                      this.getText('LT00000085', 'Rounding Stage')),
                    m(SingleSelectComponent, {
                      id: 'roundingStage',
                      items: ROUNDING_STAGES,
                      selectedItem: payload.roundingStage,
                      keyOnly: true,
                      onSelect: (item) => { payload.roundingStage = item; },
                    })),

                  dateField('LT00000010', 'Effective From', 'periodFrom', true),
                  dateField('LT00000011', 'Effective To', 'periodTo', false),

                  m('.col-12.position-relative.mb-4.g-2',
                    m('label.form-label[for=remark]', this.getText('LT00000014', 'Remark')),
                    m('textarea.form-control.form-control-bg', {
                      id: 'remark',
                      rows: 2,
                      maxlength: 1000,
                      value: payload.remark,
                      onchange: (e) => { payload.remark = e.target.value.trim(); },
                    })),

                  m('.col-12',
                    m('small.text-muted',
                      this.getText('EM00000273',
                        'A new scheme is created as DRAFT. Activate it separately once its rates are complete.'))),

                  m('.col-md-12.gap-2.d-flex.justify-content-end',
                    m("button.btn.btn-primary[type='submit']",
                      this.getText('LT00000022', 'Save')),
                    m("button.btn.btn-white[type='button']",
                      { onclick: () => { close(false); } },
                      this.getText('LT00000021', 'Close')))))))
          ,
          this.getBaseViews(),
        ],
      });
    },
  };
}
